import { ChevronDown, Layers, ListChecks } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import GapGaugeBar from "./GapGaugeBar";
import MetricStatNumbers from "./MetricStatNumbers";
import { CategoryBadge, MetricCodeBadge } from "@/components/common";

const STATUS_LABEL = {
  parent_only_na: "Group level",
  has_remark_only: "n.a",
  mixed_unit: "Mixed units",
  undefined: "Undefined",
  missing_at_parent: "Missing at parent",
};

const ConsolidatedMetricRow = ({
  row,
  isExpanded,
  onToggleDetail,
  formatYearLabel,
  formatFrameworkLabel,
}) => {
  const { t } = useTran();
  const unit = row.unit || "";
  const statusLabel = STATUS_LABEL[row.status];
  const isGroupLevelNA = row.status === "parent_only_na";
  const yearLabel = formatYearLabel ? formatYearLabel(row.year) : row.year;
  const frameworkLabel = formatFrameworkLabel
    ? formatFrameworkLabel(row.framework)
    : row.framework;
  const showGauge = Number.isFinite(row.target) && Number.isFinite(row.actual);

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border/60 bg-card/95 p-3 md:flex-row md:items-center md:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <ListChecks className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {row.title}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
            <MetricCodeBadge code={row.code} category={row.category} />
            {row.year != null && (
              <span className="rounded border border-border/70 bg-muted/40 px-1.5 py-0.5 text-muted-foreground">
                {yearLabel}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/40 px-1.5 py-0.5 text-muted-foreground">
              <Layers className="h-3 w-3" />
              {frameworkLabel}
            </span>
            <CategoryBadge category={row.category} />
            {statusLabel && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {t(statusLabel)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 justify-end md:pr-12">
        <div className="min-w-0 max-w-[200px] flex-1">
          {showGauge ? <GapGaugeBar gapPct={row.gapPct} /> : null}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <MetricStatNumbers row={row} unit={unit} naMode={isGroupLevelNA} />
        {onToggleDetail && (
          <button
            type="button"
            onClick={onToggleDetail}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            {t("View Detail")}
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default ConsolidatedMetricRow;
